import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { JSONSerializable } from "../declarations/json";

export type DebugInfo = JSONSerializable;

export type DebugInfoListener = (debugInfo: DebugInfo) => void;

export interface IDebugInfoProps<K extends PropertyKey> {
  debugInfoProps(): K[];
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function hasDebugInfoProps<O extends Object>(object: O): object is IDebugInfoProps<keyof O> & O {
  return "debugInfoProps" in object;
}

export class DebugInfoSystem {
  private readonly debugInfo = {} as DebugInfo;
  private readonly debugInfoListeners: DebugInfoListener[] = [];
  private lastDebugInfoUpdate?: DebugInfo;
  private lastDebugInfoTime_ms: DOMHighResTimeStamp = NaN;
  public debugInfoUpdateInterval_ms = 33;

  /** Returns an empty object you can add debug info to. */
  getDebugInfo(): DebugInfo {
    return this.debugInfo;
  }

  /** Call this periodically to update listeners with any pending extra debug info changes. */
  update() {
    if (this.debugInfoListeners.length == 0) {
      return;
    }

    const t0_ms = this.lastDebugInfoTime_ms;
    const tNow_ms = window.performance.now();

    const updateDebugInfoIfNecessary = () => {
      const debugInfo = this.debugInfo;
      const shouldUpdateDebugInfo = !isEqual(debugInfo, this.lastDebugInfoUpdate);
      if (shouldUpdateDebugInfo) {
        this.updateDebugInfoListeners();
        this.lastDebugInfoUpdate = cloneDeep(debugInfo);
        this.lastDebugInfoTime_ms = tNow_ms;
      }
    };

    if (isNaN(t0_ms)) {
      updateDebugInfoIfNecessary();
      return;
    }
    const dt_ms = tNow_ms - t0_ms;
    if (dt_ms > this.debugInfoUpdateInterval_ms) {
      updateDebugInfoIfNecessary();
    }
  }

  updateDebugInfoListeners() {
    const listeners = this.debugInfoListeners;
    const n = listeners.length;
    for (let i = 0; i < n; ++i) {
      listeners[i](this.debugInfo);
    }
  }

  addDebugInfoListener(listener: DebugInfoListener) {
    this.debugInfoListeners.push(listener);
  }

  removeDebugInfoListener(listener: DebugInfoListener) {
    const listeners = this.debugInfoListeners;
    let i = this.debugInfoListeners.indexOf(listener);
    if (i >= 0) {
      const n = listeners.length - 1;
      for (; i < n; ++i) {
        listeners[i] = listeners[i + 1];
      }
      listeners.length = n;
    }
  }

  /** Adds a debug info object at the provided top-level key and returns it,
   * or returns an existing debug info object at that key.
   * If an object o is supplied, also replaces the properties listed in o.debugInfoProps
   * with accessors that automatically update fields of the same name
   * in the debug info object. */
  // eslint-disable-next-line @typescript-eslint/ban-types
  addDebugInfo<O extends Object>(key: PropertyKey, o?: IDebugInfoProps<keyof O> & O): DebugInfo {
    return DebugInfoSystem.addDebugInfoRec(this.debugInfo, key, o);
  }

  /** Recursively adds debug info, per the debugInfoProps on the objects named by calling it. */
  // eslint-disable-next-line @typescript-eslint/ban-types, sonarjs/cognitive-complexity
  private static addDebugInfoRec<O extends Object>(
    parentStore: DebugInfo,
    key: PropertyKey,
    o?: IDebugInfoProps<keyof O> & O
  ): DebugInfo {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    if (typeof key === "symbol") {
      throw new Error(`Invalid DebugInfo key encountered: ${String(key)}`);
    }
    const store = (parentStore[key] ?? (parentStore[key] = {})) as DebugInfo;
    if (o == undefined) return store;
    const props = o.debugInfoProps();
    for (const prop of props) {
      const value = o[prop];
      if (typeof value === "object") {
        // eslint-disable-next-line @typescript-eslint/ban-types
        if (hasDebugInfoProps(value as Object)) {
          DebugInfoSystem.addDebugInfoRec(store, prop, value as IDebugInfoProps<any> & any);
          continue;
        }
        throw new Error(
          `Attempting to track debug info for object that doesn't implement IDebugInfoProps: ${key}/${String(prop)}`
        );
      }
      if (typeof value === "function") {
        throw new Error(`Unsupported function value type encountered while tracking DebugInfo: ${key}/${String(prop)}`);
      }

      const descriptor = Object.getOwnPropertyDescriptor(o, prop);
      if (descriptor === undefined) {
        throw new Error(`No instance property descriptor: ${key}/${String(prop)}`);
      }
      if (descriptor.set !== undefined || descriptor.get !== undefined) {
        throw new Error(`Accessor already defined: ${key}/${String(prop)}`);
      }

      store[prop as string] = value as any;
      const keyForProp = `${prop as string}_$"`;
      (o as any)[keyForProp] = value as any;
      Object.defineProperty(o, prop, {
        set: (value: any) => {
          store[prop as string] = value;
          (o as any)[keyForProp] = value as any;
        },
        get: () => {
          return store[prop as string];
        },
      });
    }
    return store;
  }
}

export const debugInfoSystem = new DebugInfoSystem();
